import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from "react-dom";
// The burst's colours: the app accent plus tints of it toward white. The
// variables live on :root, so they resolve inside the body-level portal too.
const THEME_PETALS = [
  "var(--c-primary)",
  "color-mix(in srgb, var(--c-primary) 65%, #fff)",
  "color-mix(in srgb, var(--c-primary) 40%, #fff)",
  "var(--c-primary-light)",
  "color-mix(in srgb, var(--c-primary) 82%, #fff)",
  "#fff",
  "color-mix(in srgb, var(--c-primary) 25%, #fff)",
  "color-mix(in srgb, var(--c-primary) 55%, #fff)",
];

// ─── HABIT CHECKBOX ───────────────────────────────────────────────────────────
// Animated checkbox: springy bounce + SVG checkmark draw + petal burst via portal

function PetalPortal({ origin, color, onDone }) {
  const count = 18;
  const petals = useMemo(() => Array.from({ length: count }, (_, i) => {
    const angle = (i / count) * 360 + (Math.random() - 0.5) * 22;
    const dist = 50 + Math.random() * 70;
    const rad = (angle * Math.PI) / 180;
    return {
      id: i,
      tx: Math.cos(rad) * dist,
      ty: Math.sin(rad) * dist,
      rotate: Math.random() * 360,
      w: 6 + Math.random() * 7,
      h: 4 + Math.random() * 5,
      // Theme-matched (Jade, 2026-10-16: they were always pink + black). The
      // accent and lighter tints of it, from the :root variables every
      // background sets, so the burst follows the background. A habit's own
      // colour still leads every 4th petal; a CSS-variable colour (the Planner
      // list passes var(--c-primary-deep), near-black on several backgrounds)
      // is left out in favour of the accent itself.
      color: (i % 4 === 0 && color && !String(color).startsWith("var(")) ? color : THEME_PETALS[i % THEME_PETALS.length],
      dur: 1000 + Math.random() * 450, // a touch slower (Jade, 2026-10-26; was 850 + 400)
      delay: Math.random() * 120,
    };
  }), []);

  useEffect(() => {
    const t = setTimeout(onDone, 1650);
    return () => clearTimeout(t);
  }, []);

  if (!origin) return null;

  return createPortal(
    <div style={{ position: "fixed", top: 0, left: 0, pointerEvents: "none", zIndex: 99999 }}>
      {petals.map(p => (
        <div key={p.id} style={{
          position: "fixed",
          left: origin.x - p.w / 2,
          top: origin.y - p.h / 2,
          width: p.w,
          height: p.h,
          borderRadius: "50% 50% 50% 50% / 60% 60% 40% 40%",
          background: p.color,
          // No box-shadow: it repaints every animation frame (×18 petals) and was
          // the main cause of the burst stuttering. transform+opacity stay on the
          // compositor; willChange promotes each petal to its own layer.
          willChange: "transform, opacity",
          animation: `petalFly ${p.dur}ms cubic-bezier(0.22,1,0.36,1) ${p.delay}ms both`,
          "--ptx": `${p.tx}px`,
          "--pty": `${p.ty}px`,
          "--pr": `${p.rotate}deg`,
        }} />
      ))}
    </div>,
    document.body
  );
}

// The save waits until the petal burst's fast opening is over (Jade, 2026-10-26):
// saving re-renders the whole app, and landing that in the burst's first frames
// made it skip ahead and look faster. The tick itself shows at once.
const SAVE_AFTER_BURST_MS = 350;

// Except in the computer layout (1024px and wider, App.jsx's isDesktop): there
// the Habit Tracker column with Overall Completion sits beside the list, and its
// bars grew half a second after the tick (Jade, 2026-10-28). They move with the
// tick again there. The wait stays on phones, where it was needed (the burst
// skipped) and where nothing beside the list shows the save.
const saveWaitMs = () => {
  try { return window.matchMedia("(min-width: 1024px)").matches ? 0 : SAVE_AFTER_BURST_MS; }
  catch { return SAVE_AFTER_BURST_MS; }
};

// While the save waits, the row it sits in is struck through at once too (Jade,
// 2026-11-02: the habit's name crossed out 350ms after its box). The row is marked
// straight on the DOM (no render): the name carries "hb-name" (HabitsTab), and the
// mark comes off when the save lands (the row then shows done by itself) or is
// cancelled.
const PENDING_ROW = "hb-tick-pending";
if (typeof document !== "undefined" && !document.getElementById("hb-tick-pending-css")) {
  const st = document.createElement("style");
  st.id = "hb-tick-pending-css";
  st.textContent = `.${PENDING_ROW} .hb-name { text-decoration: line-through !important; opacity: 0.6 !important; }`;
  document.head.appendChild(st);
}
const markRow = (box, on) => {
  const row = box && box.closest && box.closest(".clarity-row-item");
  if (row) row.classList.toggle(PENDING_ROW, on);
};

export const HabitCheckbox = React.memo(function HabitCheckbox({ checked, color, onChange, size = 28, dashed = false, burst = true, tickColor = "#fff" }) {
  const [bounce, setBounce] = useState(false);
  const boxRef = useRef(null);
  const prevChecked = useRef(checked);
  // Tracks where a touch started so handleTouchEnd can tell a tap from a
  // scroll/swipe that merely started or ended over the box — touchend always
  // fires on the original touchstart target, even after the finger has
  // dragged the whole page, so without this a scroll could toggle the box.
  const touchStartRef = useRef(null);

  // Ticking shows at once, and the save (onChange) runs once the burst's fast
  // opening is over (Jade, 2026-10-26: the tick lagged in the Android app). The save
  // re-renders the whole app (logs, sync, and on the phone the widgets and
  // alarms too), and the tick used to wait for all of it. Unticking still calls
  // onChange straight away: a ticked box sometimes opens a sheet instead of
  // unticking, so showing it unticked early could be wrong.
  const [optimistic, setOptimistic] = useState(false);
  const pendingRef = useRef(null); // the save timer while one is waiting
  const shown = checked || optimistic;
  // The row's early strike-through comes off only once the save has rendered (the
  // name is then struck by its own done state), so it never blinks off in between.
  useEffect(() => { if (!optimistic) markRow(boxRef.current, false); }, [optimistic]);
  useEffect(() => () => markRow(boxRef.current, false), []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = (e) => {
    e.stopPropagation();
    if (pendingRef.current) {
      // Unticked again before the save ran: drop it, nothing was saved.
      clearTimeout(pendingRef.current);
      pendingRef.current = null;
      setOptimistic(false);
      markRow(boxRef.current, false);
      return;
    }
    if (!checked) {
      const wait = saveWaitMs();
      if (wait) { setOptimistic(true); markRow(boxRef.current, true); }
      setBounce(true);
      setTimeout(() => setBounce(false), 450);
      if (burst && boxRef.current) {
        const r = boxRef.current.getBoundingClientRect();
        // Drawn by the app-level host, so a re-render or remount of this row
        // when the save lands can't cut the burst short.
        fireMCPetal({ x: r.left + r.width / 2, y: r.top + r.height / 2 }, color);
      }
      if (!wait) { onChange(e); return; }
      pendingRef.current = setTimeout(() => {
        pendingRef.current = null;
        onChange(e);
        setOptimistic(false); // batched with the parent's update, so no flicker
      }, wait);
      return;
    }
    onChange(e);
  };

  const TAP_SLOP = 10; // px of finger movement still allowed to count as a tap

  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };

  const handleTouchEnd = (e) => {
    e.preventDefault(); // block the ghost mouse click that follows touchend
    e.stopPropagation();
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (start) {
      const t = e.changedTouches[0];
      const dx = t ? Math.abs(t.clientX - start.x) : 0;
      const dy = t ? Math.abs(t.clientY - start.y) : 0;
      if (dx > TAP_SLOP || dy > TAP_SLOP) return; // finger dragged — a scroll, not a tap
    }
    handleToggle(e);
  };

  const br = Math.round(size * 0.29);

  return (
    <div style={{ position: "relative", display: "inline-flex", alignItems: "center", justifyContent: "center", width: size, height: size, flexShrink: 0 }}>

      {/* Box */}
      <div
        ref={boxRef}
        onClick={handleToggle}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        // habit-cb: on dark backgrounds an empty box's edge is lifted toward white
        // through --cb-c (App.jsx GLASS_CARD_CSS); a ticked one keeps its own fill.
        className="habit-cb"
        style={{
          "--cb-c-base": color || "#d4789a",
          width: size, height: size,
          borderRadius: br,
          border: `2px ${dashed && !shown ? "dashed" : "solid"} ${shown ? (color || "#d4789a") : "var(--cb-c, var(--cb-c-base))"}`,
          background: shown ? (color || "#d4789a") : "transparent",
          cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background 0.18s ease, border-color 0.18s ease, transform 0.18s ease",
          animation: bounce ? "hcBounce 0.42s cubic-bezier(0.34,1.56,0.64,1) both" : "none",
          flexShrink: 0,
          position: "relative",
          overflow: "visible",
          touchAction: "manipulation",
          userSelect: "none",
          WebkitUserSelect: "none",
        }}
      >
        {/* Checkmark — always in DOM, visibility driven by checked prop */}
        <svg
          width={size * 0.55}
          height={size * 0.55}
          viewBox="0 0 14 11"
          fill="none"
          style={{
            display: "block",
            overflow: "visible",
            opacity: shown ? 1 : 0,
            transform: shown ? "scale(1)" : "scale(0.5)",
            transition: "opacity 0.18s ease, transform 0.22s cubic-bezier(0.34,1.56,0.64,1)",
          }}
        >
          <polyline
            points="1.5,5.5 5.5,9.5 12.5,1.5"
            stroke={tickColor}
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </div>
  );
}, (a, b) => (
  // Re-render only when the visible state changes — the toggled checkbox's
  // `checked` flips so it always refreshes (and gets a fresh onChange closure);
  // every other habit's checkbox can safely skip re-rendering on a toggle.
  a.checked === b.checked && a.color === b.color && (a.size || 28) === (b.size || 28) && !!a.dashed === !!b.dashed && a.tickColor === b.tickColor
));

// ─── Multi-count pill button with completion animation ───────────────────────
// Fires the same petal burst + bounce as HabitCheckbox when the last tap
// brings current+1 up to target.
// Module-level petal burst emitter — survives component unmount/swap
// because it renders into a separate portal root outside the habit list.
let _mcPetalSetters = [];
let _burstSeq = 0;
function fireMCPetal(origin, color) {
  const burst = { origin, color, key: ++_burstSeq };
  _mcPetalSetters.forEach(fn => fn(list => [...list, burst]));
}
// Holds every burst still playing, so a quick tap on the next box starts its
// own burst without cutting the previous one short (Jade, 2026-10-26).
export function MCPetalHost() {
  const [bursts, setBursts] = useState([]);
  useEffect(() => {
    _mcPetalSetters.push(setBursts);
    return () => { _mcPetalSetters = _mcPetalSetters.filter(f => f !== setBursts); };
  }, []);
  return bursts.map(b => (
    <PetalPortal key={b.key} origin={b.origin} color={b.color}
      onDone={() => setBursts(list => list.filter(x => x.key !== b.key))} />
  ));
}


export const MultiCountButton = React.memo(function MultiCountButton({ current, target, color, onIncrement }) {
  const [bounce, setBounce] = useState(false);
  const btnRef = useRef(null);
  const touchStartRef = useRef(null);

  const trigger = (e) => {
    e.stopPropagation();
    const willComplete = current + 1 >= target;
    if (willComplete) {
      // Capture position & fire burst BEFORE onIncrement causes re-render
      if (btnRef.current) {
        const r = btnRef.current.getBoundingClientRect();
        fireMCPetal({ x: r.left + r.width / 2, y: r.top + r.height / 2 }, color);
      }
      setBounce(true);
      setTimeout(() => setBounce(false), 450);
    }
    onIncrement();
  };

  const TAP_SLOP = 10;
  const handleTouchStart = (e) => {
    const t = e.touches[0];
    touchStartRef.current = t ? { x: t.clientX, y: t.clientY } : null;
  };
  const handleTouchEnd = (e) => {
    e.preventDefault();
    const start = touchStartRef.current;
    touchStartRef.current = null;
    if (start) {
      const t = e.changedTouches[0];
      const dx = t ? Math.abs(t.clientX - start.x) : 0;
      const dy = t ? Math.abs(t.clientY - start.y) : 0;
      if (dx > TAP_SLOP || dy > TAP_SLOP) return;
    }
    trigger(e);
  };

  return (
    <>
      <div
        ref={btnRef}
        onClick={trigger}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        // hb-amount-btn: same look as a unit habit's AmountButton. No fill of its
        // own, so it blends into the card (Jade, 2026-10-26), and the same colour
        // lift on dark backgrounds (--amt-c, set in App.jsx's glass CSS).
        className="hb-amount-btn"
        style={{
          display: "flex", alignItems: "center", gap: 0,
          cursor: "pointer", height: 28, borderRadius: 8,
          "--amt-c-base": color || "#d4789a",
          border: "2px solid var(--amt-c, var(--amt-c-base))",
          background: "transparent",
          animation: bounce ? "mcBounce 0.42s cubic-bezier(0.34,1.56,0.64,1) both" : "none",
          touchAction: "manipulation", userSelect: "none", WebkitUserSelect: "none",
        }}
      >
        <span style={{ padding: "0 7px", fontWeight: 700, fontSize: 15, color: "var(--amt-c, var(--amt-c-base))", lineHeight: "24px" }}>+</span>
        {current > 0 && (
          <span style={{ padding: "0 7px 0 0", fontSize: 12, fontWeight: 700, color: "var(--amt-c, var(--amt-c-base))", lineHeight: "24px" }}>{current}</span>
        )}
      </div>
    </>
  );
}, (a, b) => (
  // Same idea as HabitCheckbox: only the incremented button's `current` changes,
  // so unchanged count buttons skip re-rendering on a toggle.
  a.current === b.current && a.target === b.target && a.color === b.color
));
